
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/shared/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  CreditCard,
  MessageSquare,
  BarChart3,
  UserPlus,
  TrendingUp,
  TrendingDown,
  Activity
} from 'lucide-react';

const AdminDashboard = () => {
  const [timeRange, setTimeRange] = useState('30d');

  const stats = [
    {
      title: 'Total Members',
      value: '1,234',
      change: '+12%',
      changeType: 'positive',
      icon: Users,
    },
    {
      title: 'Active Members',
      value: '1,180',
      change: '+8%',
      changeType: 'positive',
      icon: Activity,
    },
    {
      title: 'Monthly Revenue',
      value: '58.5M TZS',
      change: '+15%',
      changeType: 'positive',
      icon: TrendingUp,
    },
    {
      title: 'Pending Payments',
      value: '23',
      change: '-5%',
      changeType: 'negative',
      icon: CreditCard,
    },
  ];

  const recentMembers = [
    { id: 1, name: 'John Doe', email: 'john@example.com', status: 'active', joinDate: '2025-06-20' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', status: 'pending', joinDate: '2025-06-19' },
    { id: 3, name: 'Mike Johnson', email: 'mike@example.com', status: 'active', joinDate: '2025-06-18' },
    { id: 4, name: 'Sarah Wilson', email: 'sarah@example.com', status: 'suspended', joinDate: '2025-06-17' },
  ];

  const recentPayments = [
    { id: 1, member: 'John Doe', amount: '50,000 TZS', status: 'completed', date: '2025-06-20' },
    { id: 2, member: 'Jane Smith', amount: '25,000 TZS', status: 'pending', date: '2025-06-20' },
    { id: 3, member: 'Mike Johnson', amount: '50,000 TZS', status: 'completed', date: '2025-06-19' },
    { id: 4, member: 'Sarah Wilson', amount: '30,000 TZS', status: 'failed', date: '2025-06-19' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">
              Manage members, payments, and club operations
            </p>
          </div>
          <Button className="bg-basketball-gradient hover:opacity-90">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Member
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className={`text-sm ${
                      stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                    } flex items-center`}>
                      {stat.changeType === 'positive' ? (
                        <TrendingUp className="h-3 w-3 mr-1" />
                      ) : (
                        <TrendingDown className="h-3 w-3 mr-1" />
                      )}
                      {stat.change} from last month
                    </p>
                  </div>
                  <stat.icon className="h-8 w-8 text-basketball-orange-500" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="sms">SMS</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Members */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Recent Members</span>
                    <Link to="/admin/members">
                      <Button variant="outline" size="sm">View All</Button>
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentMembers.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-gray-600">{member.email}</p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            member.status === 'active' ? 'bg-green-100 text-green-800' :
                            member.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {member.status}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">{member.joinDate}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Payments */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Recent Payments</span>
                    <Link to="/admin/payments">
                      <Button variant="outline" size="sm">View All</Button>
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentPayments.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{payment.member}</p>
                          <p className="text-sm text-gray-600">{payment.amount}</p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {payment.status}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">{payment.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts placeholder */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">Revenue chart will be displayed here</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Members Management</span>
                  <Link to="/admin/members">
                    <Button className="bg-basketball-gradient hover:opacity-90">
                      Go to Full Members Page
                    </Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Quick overview of member management features:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">Total Members</h3>
                    <p className="text-2xl font-bold text-basketball-blue-500">1,234</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">Pending Applications</h3>
                    <p className="text-2xl font-bold text-yellow-600">12</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">Active This Month</h3>
                    <p className="text-2xl font-bold text-green-600">1,180</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Payments Management</span>
                  <Link to="/admin/payments">
                    <Button className="bg-basketball-gradient hover:opacity-90">
                      Go to Full Payments Page
                    </Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Quick overview of payment management features:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">Monthly Revenue</h3>
                    <p className="text-2xl font-bold text-green-600">58.5M TZS</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">Pending Payments</h3>
                    <p className="text-2xl font-bold text-yellow-600">23</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">Payment Success Rate</h3>
                    <p className="text-2xl font-bold text-basketball-blue-500">94%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sms">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>SMS Management</span>
                  <Link to="/admin/sms">
                    <Button className="bg-basketball-gradient hover:opacity-90">
                      Go to Full SMS Page
                    </Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Quick overview of SMS management features:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">SMS Sent Today</h3>
                    <p className="text-2xl font-bold text-blue-600">45</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">Delivery Rate</h3>
                    <p className="text-2xl font-bold text-green-600">96%</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-medium mb-2">Pending SMS</h3>
                    <p className="text-2xl font-bold text-yellow-600">8</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Reports & Analytics</span>
                  <Link to="/admin/reports">
                    <Button className="bg-basketball-gradient hover:opacity-90">
                      Go to Full Reports Page
                    </Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Quick overview of reports and analytics:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Button variant="outline" className="h-20 flex-col">
                    <Users className="h-6 w-6 mb-2" />
                    Member Report
                  </Button>
                  <Button variant="outline" className="h-20 flex-col">
                    <CreditCard className="h-6 w-6 mb-2" />
                    Financial Report
                  </Button>
                  <Button variant="outline" className="h-20 flex-col">
                    <MessageSquare className="h-6 w-6 mb-2" />
                    Communication Report
                  </Button>
                  <Button variant="outline" className="h-20 flex-col">
                    <BarChart3 className="h-6 w-6 mb-2" />
                    Analytics Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
